import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/synapse/Icon';
import { PaginationFooter } from '@/components/synapse/PaginationFooter';
import { Select } from '@/components/synapse/Select';
import { TypePill } from '@/components/synapse/TypePill';
import { EditMovementDialog } from '@/features/inventory/EditMovementDialog';
import { DeleteMovementDialog } from '@/features/inventory/DeleteMovementDialog';
import { calendarLabel, timestampLabel } from '@/features/analytics/issuingWindow';
import { useDeleteMovement, useStockActivity, useUpdateMovement } from '@/hooks/useInventory';
import { useDebounced } from '@/hooks/useDebounced';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { isKnownItemType, translateUnit } from '@/lib/itemTypeConfig';
import { cn } from '@/lib/utils';
import type { StockMovement, StockMovementLogRow, UpdateMovementPayload } from '@/types/api';

const PAGE_SIZE = 25;

type KindFilter = StockMovement['kind'] | '';

const KIND_ICON: Record<StockMovement['kind'], 'box_in' | 'box_out' | 'adjust'> = {
  STOCK_IN: 'box_in',
  ISSUE: 'box_out',
  ADJUSTMENT: 'adjust',
};

const KIND_LABEL_KEY: Record<StockMovement['kind'], string> = {
  STOCK_IN: 'activity.kindStockIn',
  ISSUE: 'activity.kindIssue',
  ADJUSTMENT: 'activity.kindAdjustment',
};

/**
 * The clinic's stock ledger, one line per movement.
 *
 * This is the screen that answers "what did I just do, and can I take it back".
 * Corrections are limited to the newest movement on each batch — enforced by
 * the API, surfaced here as `row.editable` — so the balance can never be moved
 * by an edit whose effect a later movement already assumed.
 */
export function ActivityPage() {
  const { t, i18n } = useTranslation();

  const [kind, setKind] = useState<KindFilter>('');
  const [query, setQuery] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const [editing, setEditing] = useState<StockMovementLogRow | null>(null);
  const [deleting, setDeleting] = useState<StockMovementLogRow | null>(null);

  // Debounced like the stock list's search: `query` lands in the React Query
  // key, so every keystroke would otherwise be a distinct key — a fresh rows
  // query AND a count(*) over the join, with staleTime unable to help.
  const debouncedQuery = useDebounced(query.trim(), 200);

  // Reset to page 1 during render, not in an effect: the query below is
  // subscribed during render too, so an effect would fetch the old offset under
  // the new filter first — one wasted round trip and a flash of the wrong page.
  // Same reasoning as IssueLogPanel, which has the long version of this note.
  const filterKey = [kind, debouncedQuery, from, to].join('|');
  const [pagedFilterKey, setPagedFilterKey] = useState(filterKey);
  if (filterKey !== pagedFilterKey) {
    setPagedFilterKey(filterKey);
    setPage(1);
  }
  const currentPage = filterKey === pagedFilterKey ? page : 1;

  const hasFilters = kind !== '' || query.trim() !== '' || from !== '' || to !== '';

  const { data, isLoading, isError, error, refetch, isFetching } = useStockActivity({
    kind: kind === '' ? undefined : kind,
    query: debouncedQuery || undefined,
    // The API takes instants; the inputs give calendar days. `to` covers the
    // whole day the user picked, not midnight at its start.
    from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
    to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
    page: currentPage,
    pageSize: PAGE_SIZE,
  });

  const update = useUpdateMovement();
  const remove = useDeleteMovement();

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, currentPage * PAGE_SIZE);

  function clearFilters() {
    setKind('');
    setQuery('');
    setFrom('');
    setTo('');
  }

  // `mutate` with an onSuccess callback rather than `mutateAsync`: the dialogs
  // render the failure from `error`, so awaiting would leave a rejected promise
  // with nobody to catch it.
  function submitEdit(patch: UpdateMovementPayload) {
    if (!editing) return;
    update.mutate(
      { id: editing.id, patch },
      {
        onSuccess: () => {
          setEditing(null);
          update.reset();
        },
      },
    );
  }

  function confirmDelete() {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => {
        setDeleting(null);
        remove.reset();
      },
    });
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] sm:text-[26px]">
          {t('activity.title')}
        </h1>
        <p className="text-[13.5px] text-muted">{t('activity.subtitle')}</p>
      </header>

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[180px] flex-1">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('activity.searchPlaceholder')}
            aria-label={t('activity.searchPlaceholder')}
          />
        </div>
        <div className="w-[150px]">
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value as KindFilter)}
            aria-label={t('activity.filterKind')}
          >
            <option value="">{t('activity.allKinds')}</option>
            <option value="STOCK_IN">{t('activity.kindStockIn')}</option>
            <option value="ISSUE">{t('activity.kindIssue')}</option>
            <option value="ADJUSTMENT">{t('activity.kindAdjustment')}</option>
          </Select>
        </div>
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label={t('activity.fromDate')}
          className="w-[150px]"
        />
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label={t('activity.toDate')}
          className="w-[150px]"
        />
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            {t('activity.clearFilters')}
          </Button>
        )}
      </div>

      {isLoading ? (
        <Card className="overflow-hidden p-0" aria-busy="true" aria-live="polite">
          <ul className="divide-y divide-line">
            {[0, 1, 2, 3, 4].map((i) => (
              <li key={i} className="h-[68px] animate-pulse bg-paper-2" />
            ))}
          </ul>
        </Card>
      ) : isError ? (
        <Card className="flex flex-col items-center gap-2 py-8 text-center">
          <Icon name="alert" size={20} className="text-destructive" />
          <div className="text-[13.5px] font-medium">{t('activity.couldNotLoad')}</div>
          <p className="text-[12.5px] text-muted">
            {error instanceof ApiError ? translateApiError(error) : t('errors.fallback')}
          </p>
          <Button size="sm" onClick={() => refetch()}>
            {t('activity.tryAgain')}
          </Button>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 py-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2">
            <Icon name="list" size={20} className="text-muted" />
          </span>
          <div className="text-[14px] font-semibold">
            {hasFilters ? t('activity.noMatchTitle') : t('activity.emptyTitle')}
          </div>
          <p className="max-w-sm text-[12.5px] text-muted">
            {hasFilters ? t('activity.noMatchBody') : t('activity.emptyBody')}
          </p>
          {hasFilters ? (
            <Button size="sm" variant="secondary" onClick={clearFilters}>
              {t('activity.clearFilters')}
            </Button>
          ) : (
            <Button size="sm" asChild>
              <Link to="/add">{t('activity.emptyAction')}</Link>
            </Button>
          )}
        </Card>
      ) : (
        <Card className={cn('overflow-hidden p-0', isFetching && 'opacity-95')}>
          <ul className="divide-y divide-line">
            {rows.map((row) => (
              <li key={row.id}>
                <ActivityRow
                  row={row}
                  lang={i18n.language}
                  onEdit={() => {
                    update.reset();
                    setEditing(row);
                  }}
                  onDelete={() => {
                    remove.reset();
                    setDeleting(row);
                  }}
                />
              </li>
            ))}
          </ul>
          <PaginationFooter
            page={currentPage}
            totalPages={totalPages}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={total}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
        </Card>
      )}

      {editing && (
        <EditMovementDialog
          row={editing}
          onClose={() => {
            setEditing(null);
            update.reset();
          }}
          onSubmit={submitEdit}
          isPending={update.isPending}
          error={update.error}
        />
      )}

      {deleting && (
        <DeleteMovementDialog
          row={deleting}
          onClose={() => {
            setDeleting(null);
            remove.reset();
          }}
          onConfirm={confirmDelete}
          isPending={remove.isPending}
          error={remove.error}
        />
      )}
    </div>
  );
}

interface ActivityRowProps {
  row: StockMovementLogRow;
  lang: string;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * One ledger line.
 *
 * The leading timestamp column only exists once the row is wide enough to align
 * it; below that it joins the metadata line, so the row degrades into a card
 * without a second copy of the markup — the same arrangement IssueLogPanel uses.
 *
 * The action buttons are rendered disabled rather than hidden on locked rows.
 * Hiding them would leave the rule invisible: the user would see edit controls
 * on some lines and not others with nothing anywhere explaining why. Disabled
 * buttons carry the reason in `title`, and the page subtitle states the rule
 * for touch devices that have no hover.
 */
function ActivityRow({ row, lang, onEdit, onDelete }: ActivityRowProps) {
  const { t } = useTranslation();
  const whenText = timestampLabel(row.createdAt, lang);
  const secondary = [row.itemGenericName, row.itemStrength].filter(Boolean).join(' · ');
  const signed = row.quantity > 0 ? `+${row.quantity}` : String(row.quantity);
  const lockedHint = row.editable ? undefined : t('activity.lockedHint');

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <span className="hidden w-[132px] shrink-0 pt-0.5 text-[11.5px] tabular-nums text-muted md:block">
        {whenText}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <Icon name={KIND_ICON[row.kind]} size={14} className="shrink-0 text-muted" />
          {isKnownItemType(row.itemType) ? (
            <TypePill type={row.itemType} size="sm" />
          ) : (
            <Badge tone="neutral" size="sm">
              {row.itemType}
            </Badge>
          )}
          <span className="truncate text-[13.5px] font-semibold tracking-[-0.01em]">
            {row.itemBrandName}
          </span>
          {secondary && <span className="truncate text-[12px] text-muted">{secondary}</span>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-muted">
          <span className="md:hidden">{whenText}</span>
          <span>{t(KIND_LABEL_KEY[row.kind])}</span>
          <span>· {row.batchNumber ? row.batchNumber : t('activity.noBatch')}</span>
          <span>
            ·{' '}
            {row.expiryDate
              ? t('activity.expires', { date: calendarLabel(row.expiryDate, lang) })
              : t('activity.noExpiry')}
          </span>
          <span>· {row.actorName ?? t('activity.unknownActor')}</span>
          {row.reason && <span>· {row.reason}</span>}
          {row.note && <span className="truncate">· {row.note}</span>}
        </div>
      </div>

      <span className="shrink-0 pt-0.5 text-right">
        <span
          className={cn(
            'font-mono text-[14px] font-semibold tabular-nums',
            row.quantity > 0 ? 'text-success' : 'text-destructive',
          )}
        >
          {signed}
        </span>
        <span className="ml-1 text-[11px] text-muted">{translateUnit(t, row.itemUnit)}</span>
      </span>

      <div className="flex shrink-0 items-center gap-1 pt-0.5">
        <Button
          variant="ghost"
          size="icon"
          onClick={onEdit}
          disabled={!row.editable}
          title={lockedHint}
          aria-label={
            row.editable ? t('activity.edit') : `${t('activity.edit')} — ${lockedHint ?? ''}`
          }
        >
          <Icon name="edit" size={14} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onDelete}
          disabled={!row.editable}
          title={lockedHint}
          aria-label={
            row.editable ? t('activity.delete') : `${t('activity.delete')} — ${lockedHint ?? ''}`
          }
        >
          <Icon name="trash" size={14} />
        </Button>
      </div>
    </div>
  );
}
